// CowCollarDashboard.js
import React, { useEffect, useMemo, useState } from "react";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ScatterChart,
  Scatter,
} from "recharts";

/* ---------------------------
   Single-file CSS injection
   (scoped to class .ccd- to avoid collisions)
   --------------------------- */
const injectStyles = () => {
  if (document.getElementById("cow-collar-dashboard-styles")) return;
  const css = `
  .ccd { font-family: Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial; color:#111; background:transparent; padding:16px; width: 100%; box-sizing: border-box;}
  .ccd .grid-10 { display:grid; grid-template-columns: repeat(10, 1fr); gap:12px; }
  .ccd .grid-12 { display:grid; grid-template-columns: repeat(12, 1fr); gap:12px; }
  .ccd .card { background:#fff; border:1px solid #e6e6e6; border-radius:8px; padding:10px; box-shadow:0 1px 2px rgba(0,0,0,0.03); }
  .ccd .card .card-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:4px; }
  .ccd .cardTitle { font-size:14px; font-weight:600; color:#222; }
  .ccd .cardSub { font-size:12px; color:#666; }
  .ccd .kpi-value { font-size:20px; font-weight:700; color:#111; margin-top:4px; }
  .ccd .badge { display:inline-block; padding:6px 10px; background:#efefef; border-radius:6px; font-weight:600; }
  .ccd .progress { height:8px; background:#f0f0f0; border-radius:6px; overflow:hidden; width:100%; margin-top:8px; }
  .ccd .progress > i { display:block; height:100%; background:#3b82f6; width:0%; transition:width .3s ease; }
  .ccd .farm-map { width:100%; height:480px; background:#f3f7f3; border-radius:8px; border:1px solid #e1e7e1; overflow:hidden; position:relative; }
  .ccd .kv { display:flex; justify-content:space-between; font-size:13px; margin:6px 0; color:#333; }
  .ccd .sidebar { display:flex; flex-direction:column; gap:8px; }
  .ccd .alerts { max-height:520px; overflow:auto; }
  .ccd .alert { background:#fff8e1; border:1px solid #ffe0a3; padding:8px; border-radius:6px; font-size:13px; }
  .ccd .tabs { display:flex; gap:8px; border-bottom:1px solid #eee; padding-bottom:8px; margin-bottom:12px; }
  .ccd .tab { padding:6px 10px; border-radius:6px; background:transparent; cursor:pointer; border:1px solid transparent; font-weight:600; }
  .ccd .tab.active { background:#111; color:#fff; border-color: #111; }
  .ccd .analytics-grid { display:grid; grid-template-columns: repeat(3, 1fr); gap:12px; }
  .ccd .analytics-grid-3 { display:grid; grid-template-columns: repeat(3, 1fr); gap:12px; }
  .ccd .analytics-grid-6 { display:grid; grid-template-columns: repeat(6, 1fr); gap:12px; }
  .ccd .chart-card { padding:10px; border-radius:8px; background:#fff; border:1px solid #eee; }
  .ccd .window-btn { padding:6px 8px; background:#f3f3f3; border-radius:6px; border:1px solid #ddd; cursor:pointer; }
  .ccd .window-btn.active { background:#111; color:#fff; border-color:#111; }
  .ccd .footer-row { display:grid; grid-template-columns: repeat(6, 1fr); gap:12px; align-items:center; font-size:13px; }
  .ccd .small { font-size:12px; color:#666; }
  @media (max-width:1100px){ .ccd .analytics-grid{ grid-template-columns: 1fr; } .ccd .analytics-grid-3{ grid-template-columns: 1fr; } .ccd .grid-10{ grid-template-columns: repeat(2,1fr);} .ccd .grid-12{ grid-template-columns: 1fr; } }
  `;
  const el = document.createElement("style");
  el.id = "cow-collar-dashboard-styles";
  el.innerHTML = css;
  document.head.appendChild(el);
};

/* ---------------------------
   Data and utilities (converted from your TS)
   --------------------------- */
const PADDOCKS = [
  { id: "P01", name: "North West", x: 2, y: 2, w: 48, h: 48 },
  { id: "P02", name: "North East", x: 50, y: 2, w: 48, h: 48 },
  { id: "P03", name: "South West", x: 2, y: 52, w: 48, h: 46 },
  { id: "P04", name: "South East", x: 50, y: 52, w: 48, h: 46 },
];
const ROTATION_INTERVAL_MS = 60000;
const H = (tf) => (tf === "24h" ? 24 : tf === "7d" ? 168 : 720);
const rand = (a, b) => Math.random() * (b - a) + a;

/* ---------------------------
   UI subcomponents (plain HTML/CSS)
   --------------------------- */
function KpiCard({ title, value, badge, sub }) {
  return (
    <div className="card">
      <div className="cardTitle">{title}</div>
      {badge ? <div className="badge" style={{ marginTop: 6 }}>{value}</div> : <div className="kpi-value">{value}</div>}
      {sub && <div className='small'>{sub}</div>}
    </div>
  );
}
function KpiGauge({ title, value, max = 1, format }) {
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  return (
    <div className="card">
      <div className="cardTitle">{title}</div>
      <div className="kpi-value">{format ? format(value) : value.toFixed(2)}</div>
      <div className="progress" aria-hidden>
        <i style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
function Kv({ label, value }) {
  return (
    <div className="kv">
      <span style={{ color: "#666" }}>{label}</span>
      <span style={{ fontWeight: 600 }}>{value}</span>
    </div>
  );
}
function FarmMap({ cows, paddocks, onSelectCow }) {
  const colour = (b) => (b === "grazing" ? "#2e7d32" : b === "resting" ? "#1565c0" : b === "walking" ? "#6a1b9a" : "#d32f2f");
  return (
    <div className="farm-map card" role="img" aria-label="Farm map">
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" width="100%" height="100%">
        {paddocks.map((p) => (
          <g key={p.id}>
            <rect x={p.x} y={p.y} width={p.w} height={p.h} fill="#e6f4ea" stroke="#a3d9a5" strokeWidth={0.4} />
            <text x={p.x + 1.5} y={p.y + 3.5} fontSize={2.5} fill="#275d2d">
              {p.name}
            </text>
          </g>
        ))}
        {cows.map((c) => (
          <g key={c.id} onClick={() => onSelectCow && onSelectCow(c.id)} style={{ cursor: "pointer" }}>
            <circle cx={c.x} cy={c.y} r={1.2} fill={colour(c.behaviour)} stroke="#222" strokeWidth={0.2} />
            {c.heat ? <circle cx={c.x} cy={c.y} r={1.8} fill="none" stroke="#ff8a00" strokeWidth={0.3} /> : null}
          </g>
        ))}
      </svg>
    </div>
  );
}

// bioacoustics
const taxa = ['bird', 'bee', 'culicoides', 'mosquito', 'biting_other', 'bat', 'amphibian'];

// Relative baseline proportions for mixed pasture with hedgerows + pond
const weights = {
  bird: 25,
  bee: 15,
  culicoides: 18,
  mosquito: 10,
  biting_other: 8,
  bat: 15,
  amphibian: 9,
};

/* ---------------------------
   Main single-file component
   --------------------------- */
export default function CowCollarDashboard() {
  // inject styles once
  useEffect(() => injectStyles(), []);

  // herding / state
  const [cows, setCows] = useState(() => createHerd(50, PADDOCKS[0]));
  const [selectedCowId, setSelectedCowId] = useState(null);
  const [tf, setTf] = useState("24h");
  const [padIdx, setPadIdx] = useState(0);
  const [tab, setTab] = useState("performance"); // 'performance'|'behaviour'|'bio'

  // rotation demo
  useEffect(() => {
    const r = setInterval(() => {
      setPadIdx((p) => {
        const n = (p + 1) % PADDOCKS.length;
        const pad = PADDOCKS[n];
        const minX = pad.x + 2,
          maxX = pad.x + pad.w - 2,
          minY = pad.y + 2,
          maxY = pad.y + pad.h - 2;
        setCows((prev) => prev.map((c) => ({ ...c, paddockId: pad.id, x: rand(minX, maxX), y: rand(minY, maxY) })));
        return n;
      });
    }, ROTATION_INTERVAL_MS);
    return () => clearInterval(r);
  }, []);

  // behaviour series
  const bwSeries = useMemo(() => {
    const hours = H(tf);
    return Array.from({ length: hours }, (_, h) => {
      const hod = h % 24;
      const day = hod >= 6 && hod <= 20;
      const grazingShare = day ? 0.55 + 0.15 * Math.sin((hod - 12) / 3) : 0.12;
      const rumiShare = day ? 0.18 : 0.42 + 0.12 * Math.cos((hod - 1) / 3);
      const walkShare = day ? 0.14 : 0.06;
      const restShare = Math.max(0, 1 - (grazingShare + rumiShare + walkShare));
      const sum = grazingShare + rumiShare + walkShare + restShare;
      const g = grazingShare / sum,
        r = rumiShare / sum,
        w = walkShare / sum;
      const grazing = Math.round(g * cows.length),
        ruminating = Math.round(r * cows.length),
        walking = Math.round(w * cows.length);
      const resting = Math.max(0, cows.length - (grazing + ruminating + walking));
      const intakeKgDM = grazing * 0.22 + walking * 0.05;
      const heatCows = cows.filter((c) => c.heat).length;
      const heatIndex = Math.max(
        0,
        (heatCows / Math.max(1, cows.length)) * (((hod >= 4 && hod <= 8) || (hod >= 18 && hod <= 22)) ? 1.8 : 0.8)
      );
      return {
        h,
        grazing,
        ruminating,
        resting,
        walking,
        intakeKgDM: +intakeKgDM.toFixed(2),
        heatIndex: +heatIndex.toFixed(2),
      };
    });
  }, [cows, tf]);

  const bwKpis = useMemo(() => {
    const totalIntake = bwSeries.reduce((s, r) => s + r.intakeKgDM, 0);
    const intakePerCow = totalIntake / Math.max(1, cows.length);
    const avgLyingHours =
      (bwSeries.reduce((s, r) => s + r.resting / Math.max(1, cows.length), 0) * 24) / Math.max(1, bwSeries.length);
    return { intakePerCow, totalIntake, avgLyingHours: +avgLyingHours.toFixed(1), avgBouts: 7.5 };
  }, [bwSeries, cows]);

  const lyingBoutHist = useMemo(() => {
    const bins = Array.from({ length: 10 }, (_, i) => ({ b: 3 + i, n: 0 }));
    cows.forEach(() => {
      const v = Math.max(3, Math.min(12, 7 + (Math.random() * 2 - 1) * 2.5));
      const idx = Math.min(9, Math.max(0, Math.round(v - 3)));
      bins[idx].n += 1;
    });
    return bins.map((x) => ({ b: x.b, n: x.n }));
  }, [cows]);

  // performance
  const scatterData = useMemo(
    () => cows.map((c) => ({ cowId: c.id, grazingH: +c.grazingH.toFixed(2), gain: +c.gainKgPerDay.toFixed(2) })),
    [cows]
  );
  const distData = useMemo(() => {
    const bins = Array.from({ length: 10 }, (_, i) => ({ x: 0.4 + i * 0.14, n: 0 }));
    cows.forEach((c) => {
      const idx = Math.max(0, Math.min(9, Math.floor((c.gainKgPerDay - 0.4) / 0.14)));
      bins[idx].n += 1;
    });
    return bins.map((b) => ({ bin: b.x.toFixed(2), n: b.n }));
  }, [cows]);
  const distanceData = useMemo(() => {
    const hours = H(tf);
    return Array.from({ length: hours }, (_, h) => ({ h, km: 0.2 + 0.6 * Math.abs(Math.sin((h % 24) / 3)) }));
  }, [tf]);

  function shape(t, hod) {
    if (t === 'bird')
      return (hod < 6 || hod > 18)
        ? 0.8
        : 1.0 + (hod >= 5 && hod <= 8 ? 1.2 : 0) + (hod >= 19 && hod <= 21 ? 0.8 : 0);
    if (t === 'bee') return hod >= 9 && hod <= 17 ? 1.4 : 0.2;
    if (t === 'bat') return hod >= 21 || hod <= 4 ? 1.6 : 0.1;
    if (t === 'mosquito') return hod >= 22 || hod <= 2 ? 1.5 : 0.3;
    if (t === 'culicoides') return (hod >= 21 && hod <= 23) || (hod >= 3 && hod <= 4) ? 1.6 : 0.4;
    if (t === 'biting_other') return hod >= 11 && hod <= 16 ? 1.2 : 0.3;
    if (t === 'amphibian') return hod >= 20 || hod <= 3 ? 1.1 : 0.1;
    return 1;
  }

  // Main logic — keeps your original structure intact
  const paddockDetections = useMemo(() => {
    const hours = H(tf); // assumes H(tf) returns number of hours

    return PADDOCKS.map((p) => {
      const waterBonus = p.name.includes('pond') ? 1.35 : 1.0;
      const rows = Array.from({ length: hours }, (_, h) => {
        const hod = h % 24;
        const out = { h };

        taxa.forEach((t) => {
          const base = weights[t] * shape(t, hod) * waterBonus * (0.9 + Math.random() * 0.2);
          out[t] = Math.max(0, Math.round(base / 10));
        });

        return out;
      });

      return { paddock: p, rows };
    });
  }, [tf]);

  const diversity = useMemo(() => {
    const totals = Object.fromEntries(taxa.map((t) => [t, 0]));
    paddockDetections.forEach((pd) => pd.rows.forEach((r) => taxa.forEach((t) => (totals[t] += r[t]))));
    const sum = Object.values(totals).reduce((s, n) => s + n, 0) || 1;
    const ps = Object.values(totals).map((n) => n / sum).filter((p) => p > 0);
    const richness = Object.values(totals).filter((n) => n > 0).length;
    const shannon = -ps.reduce((s, p) => s + p * Math.log(p), 0);
    const simpson = 1 - ps.reduce((s, p) => s + p * p, 0);
    const ADI = Math.min(5, 1.5 + Math.log(richness + 1));
    const NDSI = Math.max(0, Math.min(1, (totals["bird"] + totals["bat"] + totals["bee"]) / sum));
    return { totals, richness, shannon, simpson, ADI, NDSI };
  }, [paddockDetections]);

  const vectorRisk = useMemo(() => {
    const lookback = Math.min(6, Math.max(1, Math.floor(H(tf) / 24)));
    return paddockDetections.map((pd) => {
      const last = pd.rows.slice(-lookback);
      const cul = last.reduce((s, r) => s + r.culicoides, 0);
      const mos = last.reduce((s, r) => s + r.mosquito, 0);
      const bite = last.reduce((s, r) => s + r.biting_other, 0);
      const collars = 50;
      const confC = Math.max(0, Math.min(1, (cul / collars) / 5));
      const confM = Math.max(0, Math.min(1, (mos / collars) / 5));
      const confB = Math.max(0, Math.min(1, (bite / collars) / 5));
      return { paddock: pd.paddock.name, id: pd.paddock.id, culicoides: cul, mosquito: mos, biting: bite, conf: (confC + confM + confB) / 3 };
    });
  }, [paddockDetections, tf]);

  const selectedCow = useMemo(() => cows.find((c) => c.id === selectedCowId) || null, [cows, selectedCowId]);

  const herdSeries = useMemo(() => {
    const hours = (paddockDetections[0] && paddockDetections[0].rows.length) || 0;
    return Array.from({ length: hours }, (_, h) => {
      const row = { h };
      ["culicoides", "mosquito", "biting_other", "bee", "bird", "bat", "amphibian"].forEach((k) => {
        row[k] = paddockDetections.reduce((s, pd) => s + (pd.rows[h] ? pd.rows[h][k] : 0), 0);
      });
      return row;
    });
  }, [paddockDetections]);

  function WindowSelector() {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span className="small" style={{ color: "#666" }}>Window:</span>
        {["24h", "7d", "30d"].map((w) => (
          <button
            key={w}
            onClick={() => setTf(w)}
            className={`window-btn ${tf === w ? "active" : ""}`}
            style={{ borderRadius: 6, padding: "6px 8px", border: "1px solid #ddd" }}
          >
            {w}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="ccd">
      {/* KPI Row */}
      <div className="grid-10" style={{ marginBottom: 12 }}>
        <KpiCard title="Herd tracked" value={`${cows.length}/50`} />
        <KpiCard title="Avg liveweight (kg)" value={(cows.reduce((s, c) => s + c.weightKg, 0) / cows.length).toFixed(1)} />
        <KpiCard title="Daily LWG (kg/d)" value={(cows.reduce((s, c) => s + c.gainKgPerDay, 0) / cows.length).toFixed(2)} />
        <KpiCard title="Grazing (h/d)" value={(cows.reduce((s, c) => s + c.grazingH, 0) / cows.length).toFixed(1)} />
        <KpiCard title="Rumination (h/d)" value={(cows.reduce((s, c) => s + c.ruminationH, 0) / cows.length).toFixed(1)} />
        <KpiCard title="Boundary status" value="100% inside" />
        <KpiCard title="Heat alerts" value={`${cows.filter(c => c.heat).length}`} />
        <KpiGauge title="Vector risk" value={vectorRisk.reduce((s, p) => s + p.conf, 0) / Math.max(1, vectorRisk.length)} max={1} />
        <KpiCard title="Richness" value={`${diversity.richness}`} />
        <KpiCard title="Network health" value={`100% / ${(cows.reduce((s,c)=>s+c.batteryPct,0)/cows.length).toFixed(0)}% batt`} />
      </div>

      {/* Map + Cow detail + Alerts */}
      <div className="grid-12" style={{ alignItems: "start" }}>
        <div style={{ gridColumn: "span 7" }}>
          <div className="card">
            <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div className="cardTitle">Live Farm Map — Rotational Grazing Demo</div>
                <div className="cardSub">Current paddock: {PADDOCKS[padIdx].name}</div>
              </div>
              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                <WindowSelector />
              </div>
            </div>
            <div style={{ marginTop: 6 }}>
              <FarmMap cows={cows} paddocks={PADDOCKS} onSelectCow={(id) => setSelectedCowId(id)} />
            </div>
          </div>
        </div>

        <div style={{ gridColumn: "span 3" }}>
          <div className="card">
            <div className="cardTitle">Cow detail</div>
            <div style={{ marginTop: 8 }}>
              {selectedCow ? (
                <div>
                  <Kv label="ID" value={selectedCow.id} />
                  <Kv label="Weight" value={`${selectedCow.weightKg.toFixed(1)} kg`} />
                  <Kv label="Gain" value={`${selectedCow.gainKgPerDay.toFixed(2)} kg/d`} />
                  <Kv label="Grazing" value={`${selectedCow.grazingH.toFixed(1)} h/d`} />
                  <Kv label="Rumination" value={`${selectedCow.ruminationH.toFixed(1)} h/d`} />
                  <Kv label="Feed intake" value={`${selectedCow.feedIntakeKgDM.toFixed(1)} kg DM`} />
                  <Kv label="Battery" value={`${selectedCow.batteryPct.toFixed(0)}%`} />
                  <Kv label="Signal" value={`${selectedCow.rssiDbm.toFixed(0)} dBm`} />
                </div>
              ) : (
                <div style={{ color: "#888" }}>Click a cow to view details.</div>
              )}
            </div>
          </div>
        </div>

        <div style={{ gridColumn: "span 2" }}>
          <div className="card" style={{ maxHeight: 520, overflow: "hidden", display: "flex", flexDirection: "column" }}>
            <div className="cardTitle" style={{ marginBottom: 8 }}>Alerts</div>
            <div className="alerts" style={{ overflowY: "auto", paddingRight: 6 }}>
              {vectorRisk.map((v) => (
                <div key={v.id} className="alert" style={{ marginBottom: 8 }}>
                  <div style={{ fontWeight: 700 }}>Vector risk</div>
                  <div style={{ color: "#666" }}>Paddock {v.paddock}</div>
                  <div>Confidence {(v.conf * 100).toFixed(0)}%</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Analytics card with Tabs */}
      <div className="card" style={{ marginTop: 12 }}>
        <div className="cardHeader" style={{ marginBottom: 8 }}>
          <div className="cardTitle">Analytics</div>
        </div>

        {/* Tabs */}
        <div style={{ marginTop: 6 }}>
          <div className="tabs" style={{ marginBottom: 12 }}>
            <button className={`tab ${tab === "performance" ? "active" : ""}`} onClick={() => setTab("performance")}>Performance</button>
            <button className={`tab ${tab === "behaviour" ? "active" : ""}`} onClick={() => setTab("behaviour")}>Behaviour & Welfare</button>
            <button className={`tab ${tab === "bio" ? "active" : ""}`} onClick={() => setTab("bio")}>Bioacoustics</button>
          </div>

          {/* Performance */}
          {tab === "performance" && (
            <div style={{ paddingTop: 8 }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12 }}>
                <div className="chart-card">
                  <div className="chartTitle">LWG vs Grazing (scatter)</div>
                  <ResponsiveContainer width="100%" height={220}>
                    <ScatterChart margin={{ top: 10, right: 20, bottom: 20, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="grazingH" name="Grazing (h)" />
                      <YAxis dataKey="gain" name="Gain (kg/d)" />
                      <Tooltip cursor={{ strokeDasharray: "3 3" }} />
                      <Scatter data={scatterData} name="Cows" fill="#4caf50" />
                    </ScatterChart>
                  </ResponsiveContainer>
                </div>

                <div className="chart-card">
                  <div className="chartTitle">Daily gain distribution</div>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={distData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="bin" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="n" name="Cows" fill="#2196f3" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="chart-card">
                  <div className="chartTitle">Distance vs time</div>
                  <ResponsiveContainer width="100%" height={220}>
                    <AreaChart data={distanceData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="h" />
                      <YAxis />
                      <Tooltip />
                      <Area dataKey="km" name="Distance (km)" type="monotone" fill="#82ca9d" stroke="#82ca9d" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}

          {/* Behaviour */}
          {tab === "behaviour" && (
            <div style={{ paddingTop: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <div className="small" style={{ color: "#666" }}>Timeframe applies across all charts.</div>
                <WindowSelector />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(6,1fr)", gap: 12, marginBottom: 12 }}>
                <KpiCard title="Feed intake proxy (kg DM / cow / d)" value={bwKpis.intakePerCow.toFixed(1)} />
                <KpiCard title="Total intake (kg DM)" value={Math.round(bwKpis.totalIntake)} />
                <KpiCard title="Avg lying time (h / d)" value={bwKpis.avgLyingHours.toString()} />
                <KpiCard title="Lying bouts / d (est.)" value={bwKpis.avgBouts.toString()} />
                <KpiCard title="Heat index (peak)" value={Math.max(...bwSeries.map((r) => r.heatIndex)).toFixed(2)} />
                <KpiCard title="Window" value={tf} />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12 }}>
                <div className="chart-card">
                  <div className="chartTitle">Behavioural states over time (herd)</div>
                  <ResponsiveContainer width="100%" height={260}>
                    <AreaChart data={bwSeries}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="h" />
                      <YAxis />
                      <Tooltip />
                      <Area type="monotone" dataKey="grazing" stackId="1" name="Grazing" fill="#a7f3d0" stroke="#2e7d32" />
                      <Area type="monotone" dataKey="ruminating" stackId="1" name="Rumination" fill="#c7d2fe" stroke="#6366f1" />
                      <Area type="monotone" dataKey="resting" stackId="1" name="Resting" fill="#dbeafe" stroke="#1e40af" />
                      <Area type="monotone" dataKey="walking" stackId="1" name="Walking" fill="#fee2e2" stroke="#b91c1c" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                <div className="chart-card">
                  <div className="chartTitle">Feed intake proxy (kg DM / herd / hr)</div>
                  <ResponsiveContainer width="100%" height={260}>
                    <AreaChart data={bwSeries}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="h" />
                      <YAxis />
                      <Tooltip />
                      <Area type="monotone" dataKey="intakeKgDM" name="kg DM / hr" fill="#fef3c7" stroke="#d97706" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                <div className="chart-card">
                  <div className="chartTitle">Lying bouts / day (distribution)</div>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={lyingBoutHist}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="b" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="n" name="Cows" fill="#10b981" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="chart-card" style={{ marginTop: 12 }}>
                <div className="chartTitle">Oestrus index (herd)</div>
                <ResponsiveContainer width="100%" height={140}>
                  <AreaChart data={bwSeries}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="h" />
                    <YAxis />
                    <Tooltip />
                    <Area type="monotone" dataKey="heatIndex" name="Index" fill="#ffd7e2" stroke="#f43f5e" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Bioacoustics */}
          {tab === "bio" && (
            <div style={{ paddingTop: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <div className="small" style={{ color: "#666" }}>Mixed pasture with hedgerows and pond. Taxa: birds, bees, Culicoides midges, mosquitoes, other biting insects, bats, amphibians.</div>
                <WindowSelector />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(6,1fr)", gap: 12, marginBottom: 12 }}>
                <KpiCard title="Shannon H" value={diversity.shannon.toFixed(2)} sub='Measures diversity and evenness. Higher = more varied soundscape.'/>
                <KpiCard title="Simpson 1-D" value={diversity.simpson.toFixed(2)} sub='Probability two random calls are different. Near 1 = high diversity.'/>
                <KpiCard title="ADI" value={diversity.ADI.toFixed(2)} sub='Acoustic Diversity Index. Higher when many taxa occupy different frequency bands.'/>
                <KpiCard title="NDSI" value={diversity.NDSI.toFixed(2)} sub='Normalised Difference Soundscape Index. Near 1 = wildlife dominates over human noise.'/>
                <KpiCard title="Window" value={tf} />
                <KpiCard title="Taxa tracked" value={Object.keys(diversity.totals).length.toString()} />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12 }}>
                <div className="chart-card">
                  <div className="chartTitle">Herd soundscape over time (stacked)</div>
                  <ResponsiveContainer width="100%" height={260}>
                    <AreaChart data={herdSeries}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="h" />
                      <YAxis />
                      <Tooltip />
                      {["culicoides", "mosquito", "biting_other", "bee", "bird", "bat", "amphibian"].map((k, idx) => (
                        <Area key={k} type="monotone" dataKey={k} stackId="1" name={k} />
                      ))}
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                <div className="chart-card">
                  <div className="chartTitle">Vector detections by paddock</div>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={vectorRisk}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="paddock" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="culicoides" name="Culicoides" fill="#f97316" />
                      <Bar dataKey="mosquito" name="Mosquito" fill="#06b6d4" />
                      <Bar dataKey="biting" name="Biting insects" fill="#ef4444" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="chart-card">
                  <div className="chartTitle">Activity minutes per taxa (herd)</div>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={Object.entries(diversity.totals).map(([k, v]) => ({ taxa: k, minutes: v }))}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="taxa" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="minutes" name="Activity (counts proxy)" fill="#60a5fa" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer status card */}
      <div className="card" style={{ marginTop: 12 }}>
        <div style={{ padding: "8px 4px" }}>
          <div className="footer-row">
            <div>Devices online: <span style={{ fontWeight: 700 }}>50 / 50</span></div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}><span>Avg battery</span><div style={{ flex: 1 }}><div className="progress"><i style={{ width: "90%" }} /></div></div><span>90%</span></div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}><span>Mic health</span><div style={{ flex: 1 }}><div className="progress"><i style={{ width: "96%" }} /></div></div><span>96%</span></div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}><span>Packet success</span><div style={{ flex: 1 }}><div className="progress"><i style={{ width: "98%" }} /></div></div><span>98%</span></div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#666"  }}><span>Shropshire demo model • early June</span><div style={{ flex: 1 }}></div></div>
            <div style={{ textAlign: "right", color: "#666" }}>LoRaWAN cadence (demo): 5 s</div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------
   Helper: createHerd (moved here to avoid hoist issues)
   --------------------------- */
function createHerd(n = 50, startPad = PADDOCKS[0]) {
  const arr = [];
  const minX = startPad.x + 2,
    maxX = startPad.x + startPad.w - 2;
  const minY = startPad.y + 2,
    maxY = startPad.y + startPad.h - 2;
  for (let i = 1; i <= n; i++) {
    const id = `C${String(i).padStart(3, "0")}`;
    arr.push({
      id,
      weightKg: rand(520, 680),
      gainKgPerDay: rand(0.6, 1.6),
      grazingH: rand(6.5, 9.5),
      ruminationH: rand(5.0, 7.5),
      feedIntakeKgDM: rand(9, 14),
      batteryPct: rand(45, 95),
      rssiDbm: -1 * rand(90, 115),
      behaviour: Math.random() < 0.6 ? "grazing" : Math.random() < 0.5 ? "resting" : "walking",
      x: rand(minX, maxX),
      y: rand(minY, maxY),
      paddockId: startPad.id,
      heat: Math.random() < 0.06,
    });
  }
  return arr;
}
